import axios from "axios";

const BACKEND_URL = process.env.REACT_APP_BACKEND_URL;
export const API = `${BACKEND_URL}/api`;

export const api = axios.create({ baseURL: API });

/* iter101.3 — Auth token lookup is sessionStorage-first, localStorage-
   fallback. This is what makes "open impersonated user in a new tab"
   work: /impersonate-land writes the target's JWT to sessionStorage
   (per-tab scope), while the admin's own tab still uses localStorage.
   Both tabs read the "right" token from the same helper below.
   Same-origin localStorage is shared, so writing there would clobber
   the admin session — sessionStorage is intentionally per-tab. */
function getAuthToken() {
    try {
        const s = sessionStorage.getItem("ca_token");
        if (s) return s;
    } catch { /* SSR / privacy mode */ }
    return localStorage.getItem("ca_token");
}

api.interceptors.request.use((config) => {
    const token = getAuthToken();
    if (token) config.headers.Authorization = `Bearer ${token}`;
    // iter98.99fm — Emergent K8s ingress rewrites the Origin header when
    // forwarding to the backend, which breaks WebAuthn rp.id inference.
    // We echo the actual browser-facing origin here so the backend can
    // reliably compute the expected origin + rp.id.
    if (typeof window !== "undefined" && window.location?.origin) {
        config.headers["X-Client-Origin"] = window.location.origin;
    }
    return config;
});

/* Normalize FastAPI / Pydantic v2 validation errors into a single string.
   FastAPI returns:
     - 422: { detail: [{type, loc, msg, input, url}, ...] }  ← array of objects
     - other: { detail: "string" }
   Rendering the array directly via toast/JSX crashes React with
   "Objects are not valid as a React child". Flatten here once. */
api.interceptors.response.use(
    (r) => r,
    (err) => {
        const d = err?.response?.data?.detail;
        if (Array.isArray(d)) {
            err.response.data.detail = d
                .map((e) => {
                    const field = Array.isArray(e?.loc)
                        ? e.loc.filter((x) => x !== "body").join(".")
                        : "";
                    return field ? `${field}: ${e.msg}` : (e?.msg || "Invalid input");
                })
                .join("; ");
        } else if (d && typeof d === "object") {
            // iter100.282 — Preserve raw payload alongside the pretty
            // string. Some routes return structured error details
            // (e.g. `{ code, doctor_name, completeness_score }`) that
            // the caller needs verbatim to render a friendly UI. Keep
            // both: `detail_raw` for programmatic use, `detail` for
            // toasts / generic display.
            err.response.data.detail_raw = d;
            err.response.data.detail = d.msg || d.detail || JSON.stringify(d);
        }
        return Promise.reject(err);
    },
);

export default api;
